// frontend/src/components/navigation/SortDropdown.jsx
// Custom Sort Dropdown Component
// Purpose: Replace native select with customizable dropdown for better mobile UX

import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

const SortDropdown = ({ value, onChange, disabled = false, className = "", compact = false, showDirection = false }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "he";
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState("below"); // 'above' or 'below'
  const [maxHeight, setMaxHeight] = useState("320px");
  const [isLandscape, setIsLandscape] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  const sortOptions = [
    { value: "discount_desc", label: t("sort.discount_desc", "Highest Discount"), icon: "🔥" },
    { value: "discount_asc", label: t("sort.discount_asc", "Lowest Discount"), icon: "🔥" },
    { value: "price_asc", label: t("sort.price_asc", "Lowest Price"), icon: "💰" },
    { value: "price_desc", label: t("sort.price_desc", "Highest Price"), icon: "💰" },
    { value: "freshness_desc", label: t("sort.freshness_desc", "Newest First"), icon: "⏰" },
    { value: "freshness_asc", label: t("sort.freshness_asc", "Oldest First"), icon: "⏰" },
  ];

  const selectedOption = sortOptions.find((opt) => opt.value === value) || sortOptions[0];
  
  // Determine sort direction for indicator (▲ = ascending, ▼ = descending)
  const sortDirection = value?.endsWith("_asc") ? "asc" : "desc";

  // Detect landscape orientation
  useEffect(() => {
    const checkOrientation = () => {
      setIsLandscape(window.matchMedia("(orientation: landscape)").matches);
    };

    checkOrientation();
    window.addEventListener("resize", checkOrientation);
    window.addEventListener("orientationchange", checkOrientation);

    return () => {
      window.removeEventListener("resize", checkOrientation);
      window.removeEventListener("orientationchange", checkOrientation);
    };
  }, []);

  // Close dropdown on page scroll (but NOT on dropdown internal scroll)
  useEffect(() => {
    if (!isOpen) return;

    let lastScrollY = window.scrollY;
    let lastScrollX = window.scrollX;

    const handleScroll = () => {
      // Check if page actually scrolled (window scroll position changed)
      const currentScrollY = window.scrollY;
      const currentScrollX = window.scrollX;

      // Only close if page scroll position changed (not just internal dropdown scroll)
      if (currentScrollY !== lastScrollY || currentScrollX !== lastScrollX) {
        setIsOpen(false);
      }

      lastScrollY = currentScrollY;
      lastScrollX = currentScrollX;
    };

    // Listen to scroll events on window (page scroll only)
    // Use capture phase to catch early, but only on window
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isOpen]);

  // Calculate position and max-height when opening
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const calculatePosition = () => {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      
      // Estimate dropdown height (6 options + padding, no header)
      // Simple list design - more compact
      const estimatedHeight = isLandscape ? 280 : 320;
      
      // Position above if not enough space below (with safety margin)
      const safetyMargin = 20;
      const shouldPositionAbove = spaceBelow < (estimatedHeight + safetyMargin) && spaceAbove > spaceBelow;
      setPosition(shouldPositionAbove ? "above" : "below");

      // Calculate max-height based on available space (with safety margin)
      const availableSpace = shouldPositionAbove 
        ? Math.max(spaceAbove - safetyMargin, 180)
        : Math.max(spaceBelow - safetyMargin, 180);
      const calculatedMaxHeight = Math.min(availableSpace, isLandscape ? 280 : 320);
      setMaxHeight(`${Math.max(calculatedMaxHeight, 200)}px`); // Minimum 200px
      
      // Ensure dropdown doesn't overflow viewport width
      // If button is near right edge, position dropdown to the left
      if (buttonRect.right > viewportWidth - 300) {
        // Will be handled by CSS classes
      }
    };

    // Calculate immediately
    calculatePosition();
    
    // Recalculate on resize only (not scroll - we close on scroll instead)
    window.addEventListener("resize", calculatePosition);

    return () => {
      window.removeEventListener("resize", calculatePosition);
    };
  }, [isOpen, isLandscape]);

  // Scroll selected option into view when opening
  useEffect(() => {
    if (isOpen && dropdownRef.current) {
      const selectedButton = dropdownRef.current.querySelector('[aria-selected="true"]');
      if (selectedButton) {
        setTimeout(() => {
          selectedButton.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }, 100);
      }
    }
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen]);

  const handleSelect = (optionValue) => {
    // Always pass event-like object format for consistency
    if (typeof onChange === 'function') {
      onChange({ target: { value: optionValue } });
    }
    setIsOpen(false);
  };

  return (
    <div className={`relative ${className}`}>
      {/* Button - Simple, clean design like H&M/Zara */}
      <button
        ref={buttonRef}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          flex items-center gap-2
          ${compact ? "px-2 py-1 text-xs" : "px-3 py-2 sm:px-3 sm:py-1.5 text-sm"}
          font-normal text-gray-700
          ${compact ? "border-0 bg-gray-700 text-gray-200 hover:bg-gray-600" : "border border-gray-300 rounded-md bg-white hover:bg-gray-50"}
          focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500
          transition-colors duration-150
          disabled:opacity-50 disabled:cursor-not-allowed
          touch-target ${compact ? "min-w-[80px] min-h-[36px]" : "min-w-[140px] sm:min-w-[130px] min-h-[40px] sm:min-h-[36px]"}
        `}
        aria-label={t("filter.sort_by_label", "Sort by")}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {!compact && (
          <span className={`flex-1 ${isRTL ? "text-right" : "text-left"} truncate`}>
            {selectedOption.label}
          </span>
        )}
        {compact && (
          <>
            <span className="text-sm flex-shrink-0">{selectedOption.icon}</span>
            <span className="text-xs truncate max-w-[50px]">
              {selectedOption.label.split(' ')[0]}
            </span>
          </>
        )}
        <span className={`${compact ? "text-[10px]" : "text-gray-400 text-xs"} transition-transform duration-200 flex-shrink-0 ${isOpen ? "rotate-180" : ""}`}>
          ▼
        </span>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div
            className="fixed inset-0 bg-black/20 z-40 sm:hidden"
            onClick={() => setIsOpen(false)}
          />

          {/* Dropdown List - Simple, clean like fashion retailers */}
          <div
            ref={dropdownRef}
            className={`
              absolute ${isRTL 
                ? (isLandscape ? "left-0" : "left-0 sm:left-auto sm:right-0")
                : (isLandscape ? "right-0" : "right-0 sm:right-auto sm:left-0")
              }
              w-64 sm:w-56
              ${position === "above" ? "bottom-full mb-1" : "top-full mt-1"}
              bg-white rounded-lg shadow-lg border border-gray-200
              z-[60] overflow-hidden
              transform transition-all duration-150 ease-out
              opacity-100 scale-100
            `}
            role="listbox"
            style={{ maxHeight: maxHeight }}
          >

            {/* Options - Simple list, no icons, clean design */}
            <div 
              className={`
                ${isLandscape ? "py-1" : "py-1"} 
                overflow-y-auto overflow-x-hidden
                scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100
              `}
              style={{ maxHeight: maxHeight }}
            >
              {sortOptions.map((option) => {
                const isSelected = option.value === value;
                return (
                  <button
                    key={option.value}
                    onClick={() => handleSelect(option.value)}
                    className={`
                      w-full flex items-center justify-between px-4
                      ${isLandscape ? "py-2.5" : "py-3 sm:py-2.5"}
                      ${isLandscape ? "text-sm" : "text-sm"} 
                      font-normal
                      transition-colors duration-100
                      touch-target 
                      ${isLandscape ? "min-h-[44px]" : "min-h-[48px] sm:min-h-[44px]"}
                      ${
                        isSelected
                          ? "bg-gray-100 text-gray-900 font-medium"
                          : "text-gray-700 hover:bg-gray-50"
                      }
                    `}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span className={`flex-1 ${isRTL ? "text-right" : "text-left"}`}>
                      {option.label}
                    </span>
                    {isSelected && (
                      <span className="text-blue-600 text-sm flex-shrink-0 ml-2">
                        ✓
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SortDropdown;

